/**
 * OmniLeagues — Semprion HQ Admin Console (Phase B–E shell)
 *
 * Single-file admin surface with side nav + tabbed content.
 * Tabs: Overview · Customers · Billing · Tickets · System Messages · HQ Staff · Audit
 *
 * Impersonation is "quiet" — clicking the impersonate icon on a customer
 * row POSTs /admin/impersonate/start, the cookies are swapped server-side,
 * then we route to /dashboard. No banner, no fanfare. A small persistent
 * pill in the top-right of the Dashboard lets staff exit impersonation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import {
  Users, Building2, Crown, CreditCard, LifeBuoy, Megaphone, UserCog,
  History, Search, LogIn, Shield, Loader2, LogOut, AlertCircle,
  CheckCircle2, XCircle, Save, Plus, Trash2, MessageCircle, SlidersHorizontal,
  ChevronDown, ChevronUp, Settings2, Image as ImageIcon, Wrench, LayoutGrid,
  DollarSign, PlayCircle, Clock, Percent, KeyRound,
  RotateCcw,
  Archive,
  Download,
  Pencil,} from 'lucide-react';
import SupportCommandCenter from './SupportCommandCenter';
import '../styles/bop-admin-skin.css';
import OLMembersPanel from '../components/OLMembersPanel';
import OLFinanceFeaturesPane from '../components/admin/OLFinanceFeaturesPane';
import {
  useFeatureRegistry, TierFeatureGrants, FeatureRail, CustomerDetail, tierGrants,
} from '../components/admin/SemprionEntitlements';
import { ROLE_TYPES } from '../config/pageRegistry';
import { PlatformSetupCard, HqPaymentsCard, SquarePlatformCard } from '../components/settings/PaymentsSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The console's primary destinations. Infrequent operator utilities live under
// TOOLS below, the way BOP groups its own Tools menu, so the main list stays
// short enough to scan.
const NAV = [
  { key: 'overview',   label: 'Overview',        icon: Shield },
  { key: 'customers',  label: 'Customers',       icon: Users },
  { key: 'billing',    label: 'Billing',         icon: CreditCard },
  { key: 'support',    label: 'Support Center',  icon: MessageCircle },
];

// "Tickets (legacy)" used to sit in the main list and is deliberately gone: it
// read the `support_tickets` collection while the Support Center's Tickets tab
// read `helpdesk_tickets`, and on PROD the legacy one held ALL the real tickets
// while the new one was empty. Rather than delete the pane and hide live
// tickets, the helpdesk now reads the same `support_tickets` collection, so the
// Support Center's Tickets tab IS that queue. One queue, one collection.
const TOOLS_NAV = [
  { key: 'messages',   label: 'System Messages', icon: Megaphone },
  { key: 'staff',      label: 'User Management', icon: UserCog },
  { key: 'catalog',    label: 'Page Catalog',    icon: LayoutGrid },
  { key: 'olfin',      label: 'OL Finance features', icon: SlidersHorizontal },
  { key: 'audit',      label: 'Audit Log',       icon: History },
];

export default function AdminConsole() {
  const { user, logout, isSemprionAdmin } = useAuth();
  const navigate = useNavigate();
  // The pane IS the URL, so it can be linked, bookmarked and navigated with
  // the back button. An unknown segment falls back to Overview rather than
  // rendering an empty console.
  const { tab: tabParam } = useParams();
  const PANES = ['overview', 'customers', 'billing', 'support',
                 'catalog', 'messages', 'staff', 'audit', 'olfin'];
  const tab = PANES.includes(tabParam) ? tabParam : 'overview';
  const setTab = (next) => navigate(next === 'overview' ? '/admin' : `/admin/${next}`);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!isSemprionAdmin) { navigate('/dashboard'); return; }
    axios.get(`${API_URL}/api/admin/me`).then(({ data }) => setMe(data)).catch(() => {});
  }, [isSemprionAdmin, navigate]);

  if (!isSemprionAdmin) return null;

  return (
    // `bop-skin` scopes BackOfficePro's design tokens + fonts to this console
    // only, so the rest of OL keeps its own theme. See styles/bop-admin-skin.css.
    <div className="bop-skin min-h-screen bg-background text-foreground flex">
      {/* Sidebar — BOP's dark rail (#2D3748), 224px as the style guide specifies.
          It was w-60 and its brand was a Crown icon, which is why this console
          looked unlike every other OL page (AJ, 2026-10-18). The markup below is
          the same brand block DashboardSidebar renders, so the two shells cannot
          drift on the one thing a viewer sees first. Per BOP's UI_GUIDE §3 the
          platform operator shows NO badge — it has no subscription. */}
      <aside className="w-56 bg-[#2D3748] text-white flex flex-col">
        <div className="border-b border-gray-600 p-4">
          <div className="flex items-center gap-3">
            <img src="/semprion_logo.png" alt="OmniLeagues"
                 className="w-9 h-9 object-contain flex-shrink-0" data-testid="app-logo" />
            <div className="flex flex-col min-w-0">
              <span className="text-base font-black tracking-tight truncate">OmniLeagues</span>
              <span className="text-[10px] text-blue-400 uppercase tracking-wider font-semibold">
                Semprion HQ
              </span>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {NAV.map(n => <NavButton key={n.key} n={n} tab={tab} setTab={setTab} />)}

          <div className="pt-3 mt-2 border-t border-white/10">
            <div className="px-3 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500">
              <Wrench className="w-3 h-3" /> Tools
            </div>
            {TOOLS_NAV.map(n => <NavButton key={n.key} n={n} tab={tab} setTab={setTab} />)}
          </div>
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* BOP puts the account controls in the white top header, upper right
            — never in the sidebar foot (AJ, 2026-10-18). Same 56px bar and
            #DCDFE4 rule as the dashboard, measured from app.bophq.com. */}
        <header className="bg-white border-b border-[#DCDFE4] sticky top-0 z-30 h-14 box-border">
          <div className="w-full px-6 lg:px-8 h-full flex items-center justify-between gap-3">
            <div className="text-base font-semibold text-foreground">Semprion HQ</div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right hidden sm:block leading-tight">
                <div className="text-sm font-medium text-foreground" data-testid="admin-user-name">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Semprion'}
                </div>
                <div className="text-xs text-muted-foreground truncate max-w-[220px]"
                     data-testid="admin-user-email">{user?.email}</div>
              </div>
              <Button size="icon" variant="ghost" title="Sign out" data-testid="admin-logout-btn"
                onClick={() => { logout(); navigate('/login'); }}
                className="text-muted-foreground hover:text-foreground">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        <div className="w-full px-6 lg:px-8 py-8">
          {tab === 'overview'  && <OverviewPane me={me} onJump={setTab} />}
          {tab === 'customers' && <CustomersPane />}
          {tab === 'billing'   && <BillingPane />}
          {tab === 'support'   && <SupportCommandCenter />}
          {tab === 'catalog'   && <PageCatalogPane />}
          {tab === 'olfin'     && <OLFinanceFeaturesPane />}
          {tab === 'messages'  && <MessagesPane />}
          {tab === 'staff'     && <MembersPane />}
          {tab === 'audit'     && <AuditPane />}
        </div>
      </main>
    </div>
  );
}

/* ---------- Overview ---------- */
function OverviewPane({ me, onJump }) {
  const stats = me?.stats || { leagues: 0, organizations: 0, families: 0 };
  const [recentTickets, setRecentTickets] = useState([]);
  const [openTickets, setOpenTickets] = useState(0);
  useEffect(() => {
    // The list is sliced to five for display, so it CANNOT also be the source
    // of the headline count -- doing that capped "Open tickets" at five and
    // silently under-reported the moment there were more. The figure comes
    // from the stats endpoint, which counts the whole collection.
    axios.get(`${API_URL}/api/admin/tickets`)
      .then(({ data }) => {
        const all = data.tickets || [];
        // Open work first: a recent-tickets card that leads with closed rows
        // buries the only ones anybody needs to act on.
        const rank = (t) => (t.status === 'closed' ? 1 : 0);
        setRecentTickets([...all].sort((a, b) => rank(a) - rank(b)).slice(0, 5));
      })
      .catch(() => {});
    axios.get(`${API_URL}/api/helpdesk/admin/enhanced-stats`)
      .then(({ data }) => setOpenTickets(data.open_tickets ?? 0))
      .catch(() => {});
  }, []);
  return (
    <div className="space-y-4">
      <Header title="Overview" subtitle="Semprion HQ dashboard — at-a-glance customer health" />
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Leagues"        value={stats.leagues}        icon={Crown} onClick={() => onJump('customers')} />
        <StatCard label="Organizations"  value={stats.organizations}  icon={Building2} onClick={() => onJump('customers')} />
        <StatCard label="Family accounts" value={stats.families}      icon={Users} onClick={() => onJump('customers')} />
        <StatCard label="Open tickets"   value={openTickets}          icon={LifeBuoy} onClick={() => onJump('support')} />
      </div>

      {/* Helpdesk at-a-glance — the 5 most recent tickets with click-through
          to the full queue. Makes the helpdesk implementation obvious from
          the moment a super-user lands on /admin. */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LifeBuoy className="h-4 w-4 text-blue-600" />
              <div className="text-sm font-semibold">Helpdesk — Recent Tickets</div>
            </div>
            <Button size="sm" variant="ghost" className="text-xs text-blue-600 hover:text-amber-300" onClick={() => onJump('tickets')} data-testid="overview-open-tickets">
              Open queue →
            </Button>
          </div>
          {recentTickets.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4 text-center">No tickets yet. Customer submissions will appear here and in the Tickets queue.</div>
          ) : (
            <div className="space-y-1">
              {recentTickets.map(t => (
                <div key={t.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/40 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground truncate">{t.subject}</div>
                    <div className="text-muted-foreground truncate">
                      {t.created_by_name || t.created_by_email} · {t.priority}
                      {t.context?.active_tab && <span> · <span className="text-muted-foreground">tab: {t.context.active_tab}</span></span>}
                    </div>
                  </div>
                  <Badge className={
                    t.status === 'open' ? 'bg-blue-500/10 text-blue-600' :
                    t.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                    'bg-emerald-500/15 text-emerald-400'
                  }>{t.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardContent className="p-4 text-sm text-muted-foreground">
          This console is Semprion-only. Use the nav on the left to jump between customer directory,
          billing, support tickets, system messages, HQ staff, and the audit log.
        </CardContent>
      </Card>
    </div>
  );
}

function NavButton({ n, tab, setTab }) {
  const Icon = n.icon;
  const active = tab === n.key;
  return (
    <button onClick={() => setTab(n.key)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
        active ? 'bg-zinc-800 text-white' : 'text-gray-400 hover:bg-zinc-700 hover:text-white'
      }`}
      data-testid={`admin-nav-${n.key}`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium text-sm">{n.label}</span>
    </button>
  );
}

function StatCard({ label, value, icon: Icon, onClick, hint }) {
  return (
    <button onClick={onClick} className="text-left">
      <Card className="bg-card border-border hover:border-blue-500/40 transition">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold mt-1">{value}</div>
            {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
          </div>
          <Icon className="h-8 w-8 text-blue-500/60" />
        </CardContent>
      </Card>
    </button>
  );
}

/* ---------- Customers ---------- */
/**
 * Customer Management, built to BackOfficePro's Companies page.
 *
 * BOP: a clickable card header per company — a 16x12 logo tile, a text-lg title
 * with an inline tier badge, and a description line of contact email plus store
 * count — expanding into Company Logo | Stores (N) | Feature Access.
 *
 * OL nests league -> organizations where BOP nests company -> stores, so the
 * description line reads "N organizations" for a league and the sports it runs
 * for a single club. A club sold on its own is a customer whose only entity is
 * itself, which is BOP's one-store company.
 *
 * No stat cards here on purpose: BOP's Companies page has none, and they were
 * part of why this page did not read like BOP's. The figures live on Billing,
 * where BOP puts them.
 */
function CustomersPane() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showFamilies, setShowFamilies] = useState(false);
  // BOP's delete flow: a dialog that will not arm until the exact name is
  // typed. The server checks the name too, so calling the API directly cannot
  // skip it (BOP's check is browser-only).
  const [deleting, setDeleting] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [adding, setAdding] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ type: 'organization', name: '', contact_email: '', tier: '' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [exportingId, setExportingId] = useState(null);
  const features = useFeatureRegistry();
  const [tiers, setTiers] = useState([]);

  /** BOP downloads the tenant export as a JSON file; same here. */
  const exportCustomer = async (r) => {
    setExportingId(r.id);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/customers/${r.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(r.name || 'customer').replace(/[^a-z0-9]/gi, '_')}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not export this customer.');
    }
    setExportingId(null);
  };

  const createCustomer = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/customers`, newCustomer);
      setAdding(false);
      setNewCustomer({ type: 'organization', name: '', contact_email: '', tier: '' });
      setErr('');
      await loadCustomers();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not create this customer.');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const path = editing.type === 'league' ? 'leagues' : 'organizations';
    try {
      await axios.put(`${API_URL}/api/${path}/${editing.id}`, editForm);
      setEditing(null); setEditForm({}); setErr('');
      await loadCustomers();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save this customer.');
    }
  };

  const disableCustomer = async () => {
    if (!deleting) return;
    try {
      const { data } = await axios.delete(`${API_URL}/api/admin/customers/${deleting.id}`,
        { data: { confirm_name: confirmName } });
      setErr('');
      setDeleting(null); setConfirmName('');
      await loadCustomers();
      window.alert(`${data.message}. Recovery window: ${data.recovery_window}.`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not disable this customer.');
    }
  };

  const restoreCustomer = async (r) => {
    try {
      await axios.post(`${API_URL}/api/admin/customers/${r.id}/restore`);
      setErr('');
      await loadCustomers();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not restore this customer.');
    }
  };

  const loadCustomers = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/customers`);
    setRows(data.customers || []);
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => {
    axios.get(`${API_URL}/api/billing/tiers`)
      .then(({ data }) => setTiers(data.tiers || [])).catch(() => setTiers([]));
  }, []);

  const filtered = useMemo(() => {
    const s2 = q.toLowerCase().trim();
    let base = showFamilies ? rows : rows.filter(r => r.type !== 'family');
    if (statusFilter === 'on_plan') base = base.filter(r => r.tier);
    else if (statusFilter === 'unmanaged') base = base.filter(r => r.type !== 'family' && !r.tier);
    else if (statusFilter !== 'all') base = base.filter(r => r.subscription_status === statusFilter);
    if (!s2) return base;
    return base.filter(r => (r.name || '').toLowerCase().includes(s2)
      || (r.email || '').toLowerCase().includes(s2)
      || (r.slug || '').toLowerCase().includes(s2));
  }, [rows, q, showFamilies, statusFilter]);

  const orgsOf = useCallback((cust) => (cust.type === 'league'
    ? rows.filter(r => r.type === 'organization' && r.league_id === cust.id)
    : [cust]), [rows]);

  const impersonate = async (row) => {
    if (!row.impersonate_user_id) return;
    setBusyId(row.id); setErr('');
    try {
      await axios.post(`${API_URL}/api/admin/impersonate/start`, { target_user_id: row.impersonate_user_id });
      window.location.href = '/dashboard';
    } catch (e) {
      setErr(e.response?.data?.detail || 'Impersonation failed');
      setBusyId(null);
    }
  };

  const planLabel = (r) => (r.tier
    ? (tiers.find(t => t.tier === r.tier)?.label || r.tier)
    : null);

  return (
    <div className="space-y-4">
      {/* BOP's CompanyManagementPage header, verbatim. Page for page: BOP's
          Billing uses text-2xl font-bold and its Companies page uses
          text-3xl font-black — OL matches each page as it is, rather than
          normalising the two (AJ, 2026-10-18). */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Customer Management</h1>
          <p className="text-muted-foreground mt-1">Manage leagues, organizations, their plans and feature access</p>
        </div>
        <Button className="rounded-sm" onClick={() => setAdding(true)} data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>
      {err && <Alert msg={err} type="error" />}

      {/* Toolbar — BOP's search + status filter + visibility switch */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search customers by name, email or slug..."
            className="pl-10 rounded-sm" data-testid="admin-customer-search" />
        </div>
        <span className="text-sm text-muted-foreground">Status:</span>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          data-testid="customer-status-filter"
          className="h-9 text-sm rounded-sm border border-input bg-background px-2">
          <option value="all">All</option>
          <option value="on_plan">On a plan</option>
          <option value="unmanaged">Unmanaged</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
        </select>
        {/* BOP uses a Switch here ("Show Trial"), not a checkbox. */}
        <div className="flex items-center gap-2">
          <Switch checked={showFamilies} onCheckedChange={setShowFamilies}
            data-testid="toggle-families" id="toggle-families" />
          <label htmlFor="toggle-families" className="text-sm text-muted-foreground cursor-pointer">
            Show family accounts
          </label>
        </div>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
             onClick={() => setAdding(false)}>
          <Card className="w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}
                data-testid="add-customer-dialog">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-base font-semibold">Add Customer</h3>
              <div>
                <label className="text-xs text-muted-foreground">Type</label>
                <select value={newCustomer.type} data-testid="new-customer-type"
                  onChange={e => setNewCustomer(c => ({ ...c, type: e.target.value }))}
                  className="w-full h-9 text-sm rounded-sm border border-input bg-background px-2 mt-1">
                  <option value="organization">Organization (a single club)</option>
                  <option value="league">League (bills for its organizations)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <Input value={newCustomer.name} autoFocus data-testid="new-customer-name"
                  onChange={e => setNewCustomer(c => ({ ...c, name: e.target.value }))}
                  className="rounded-sm mt-1" placeholder="South Wake Sabres" />
              </div>
              {newCustomer.type === 'organization' && (
                <div>
                  <label className="text-xs text-muted-foreground">Contact email</label>
                  <Input type="email" value={newCustomer.contact_email}
                    onChange={e => setNewCustomer(c => ({ ...c, contact_email: e.target.value }))}
                    className="rounded-sm mt-1" />
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground">Plan</label>
                <select value={newCustomer.tier} data-testid="new-customer-tier"
                  onChange={e => setNewCustomer(c => ({ ...c, tier: e.target.value }))}
                  className="w-full h-9 text-sm rounded-sm border border-input bg-background px-2 mt-1">
                  <option value="">No plan (unmanaged — every feature on)</option>
                  {tiers.map(t => <option key={t.tier} value={t.tier}>{t.label || t.tier}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" onClick={createCustomer} data-testid="create-customer-btn"
                  disabled={!newCustomer.name.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">Create</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
             onClick={() => { setEditing(null); setEditForm({}); }}>
          <Card className="w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}
                data-testid="edit-customer-dialog">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-base font-semibold">Edit {editing.name}</h3>
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <Input value={editForm.name ?? ''} autoFocus data-testid="edit-customer-name"
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="rounded-sm mt-1" />
              </div>
              <p className="text-xs text-muted-foreground">
                Plan, feature access and discounts are on the customer&apos;s own row below.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm"
                  onClick={() => { setEditing(null); setEditForm({}); }}>Cancel</Button>
                <Button size="sm" onClick={saveEdit} data-testid="save-customer-btn"
                  disabled={!(editForm.name || '').trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">Save</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
             onClick={() => { setDeleting(null); setConfirmName(''); }}>
          <Card className="w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}
                data-testid="delete-customer-dialog">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-base font-semibold">Disable {deleting.name}?</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Nothing is erased. The customer is disabled and scheduled for deletion,
                    and can be restored from this page during the recovery window.
                  </p>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  Type <strong className="text-foreground">{deleting.name}</strong> to confirm
                </label>
                <Input value={confirmName} autoFocus data-testid="delete-confirm-name"
                  onChange={e => setConfirmName(e.target.value)}
                  className="mt-1 rounded-sm" placeholder={deleting.name} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm"
                  onClick={() => { setDeleting(null); setConfirmName(''); }}>Cancel</Button>
                <Button size="sm" data-testid="delete-confirm-btn"
                  disabled={confirmName.trim().toLowerCase() !== (deleting.name || '').trim().toLowerCase()}
                  onClick={disableCustomer}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
                  Disable customer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(q || statusFilter !== 'all') && (
        <p className="text-sm text-muted-foreground">
          Showing {filtered.length} {filtered.length === 1 ? 'customer' : 'customers'}
          {statusFilter !== 'all' && ` with status "${statusFilter}"`}
          {q && ` matching "${q}"`}
        </p>
      )}

      {filtered.length === 0 ? (
        <Card className="border border-border rounded-lg">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No customers match your filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map(r => {
            const key = `${r.type}-${r.id}`;
            const isOpen = expanded === key;
            const billable = r.type !== 'family';
            const kids = billable ? orgsOf(r) : [];
            const logo = r.logo_url
              ? (r.logo_url.startsWith('http') ? r.logo_url : `${API_URL}${r.logo_url}`)
              : null;
            return (
              <Card key={key} id={`customer-card-${r.id}`}
                className={`border rounded-lg overflow-hidden ${
                  r.disabled ? 'border-red-300 bg-red-50/30' : 'border-border'}`}>
                <div
                  className={`p-4 ${billable ? 'cursor-pointer hover:bg-zinc-50 transition-colors' : ''}`}
                  onClick={() => billable && setExpanded(isOpen ? null : key)}
                  data-testid={`expand-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-16 h-12 bg-white border border-border rounded-sm flex items-center justify-center overflow-hidden shrink-0">
                        {logo
                          ? <img src={logo} alt={r.name} className="max-w-full max-h-full object-contain" />
                          : <ImageIcon className="w-6 h-6 text-zinc-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-semibold flex items-center gap-2 truncate">
                          <span className={`truncate ${r.disabled ? 'line-through opacity-70' : ''}`}>
                            {r.name || '—'}
                          </span>
                          {r.disabled && (
                            <Badge className="bg-red-100 text-red-700 text-xs rounded-sm">
                              Scheduled for deletion
                            </Badge>
                          )}
                          {planLabel(r)
                            ? <Badge className="bg-blue-100 text-blue-700 text-xs rounded-sm">{planLabel(r)}</Badge>
                            : billable && <Badge variant="outline" className="text-xs rounded-sm text-muted-foreground">No plan</Badge>}
                          {r.subscription_status && r.subscription_status !== 'active' && (
                            <Badge variant="outline" className="text-xs rounded-sm">{r.subscription_status}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="truncate">
                            {r.contact_email || r.email || r.location || r.slug || 'No contact on file'}
                          </span>
                          {r.type === 'league' && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Building2 className="w-3 h-3" />{kids.length} organizations
                            </span>
                          )}
                          {r.type === 'organization' && Array.isArray(r.sports) && r.sports.length > 0 && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Crown className="w-3 h-3" />{r.sports.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.impersonate_user_id && (
                        <Button variant="outline" size="sm" className="rounded-sm"
                          onClick={(e) => { e.stopPropagation(); impersonate(r); }}
                          disabled={busyId === r.id} data-testid={`impersonate-${r.id}`}>
                          {busyId === r.id
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <LogIn className="w-4 h-4 mr-1" />} View as
                        </Button>
                      )}
                      {/* BOP's row order: download, pencil, archive, chevron. */}
                      {billable && !r.disabled && (
                        <Button variant="ghost" size="sm" className="rounded-sm text-blue-600 hover:bg-blue-50"
                          title="Export all customer data (JSON)" data-testid={`export-customer-${r.id}`}
                          disabled={exportingId === r.id}
                          onClick={(e) => { e.stopPropagation(); exportCustomer(r); }}>
                          {exportingId === r.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Download className="w-4 h-4" />}
                        </Button>
                      )}
                      {billable && !r.disabled && (
                        <Button variant="ghost" size="sm" className="rounded-sm text-blue-600 hover:bg-blue-50"
                          title="Edit this customer" data-testid={`edit-customer-${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(r);
                            setEditForm({ name: r.name || '' });
                          }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {billable && (r.disabled ? (
                        <Button variant="ghost" size="sm" data-testid={`restore-customer-${r.id}`}
                          className="rounded-sm text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          title="Restore — nothing was erased"
                          onClick={(e) => { e.stopPropagation(); restoreCustomer(r); }}>
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      ) : (
                        /* BOP uses Archive at text-red-500 for an ACTIVE company; Trash2
                           appears only on an already-disabled one as "permanently delete
                           now". Since OL offers no hard delete, Trash2 never applies. */
                        <Button variant="ghost" size="sm" data-testid={`delete-customer-${r.id}`}
                          className="rounded-sm text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Disable and schedule for deletion"
                          onClick={(e) => { e.stopPropagation(); setDeleting(r); setConfirmName(''); }}>
                          <Archive className="w-4 h-4" />
                        </Button>
                      ))}
                      {billable && (isOpen
                        ? <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        : <ChevronDown className="h-5 w-5 text-muted-foreground" />)}
                    </div>
                  </div>
                </div>

                {isOpen && billable && (
                  <div className="border-t border-border p-4 bg-zinc-50/50">
                    <CustomerDetail
                      customer={r}
                      organizations={kids}
                      features={features}
                      tiers={tiers}
                      onChanged={loadCustomers}
                      onImpersonate={impersonate}
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TypeBadge({ t }) {
  const map = {
    league:       'bg-purple-500/15 text-purple-300 border-purple-500/30',
    organization: 'bg-blue-500/10 text-amber-300 border-amber-500/30',
    family:       'bg-slate-500/15 text-slate-700 border-slate-500/30',
  };
  return <span className={`text-[10px] border px-2 py-0.5 rounded uppercase tracking-wide ${map[t] || map.family}`}>{t}</span>;
}
function StatusBadge({ s }) {
  const map = {
    trial:    'bg-blue-500/15 text-blue-300',
    active:   'bg-emerald-500/15 text-emerald-300',
    past_due: 'bg-blue-500/10 text-amber-300',
    canceled: 'bg-red-500/15 text-red-300',
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded ${map[s] || 'bg-slate-100 text-slate-600'}`}>{s || 'n/a'}</span>;
}

/* Per-plan accent, mirroring BOP's coloured plan cards (Popular blue, Full
   Access purple). Keyed by OL's plan slugs; anything new falls back to grey. */
const TIER_ACCENT = {
  free:     { border: 'border-zinc-200',   badge: 'bg-zinc-500 text-white' },
  starter:  { border: 'border-zinc-300',   badge: 'bg-zinc-700 text-white' },
  pro:      { border: 'border-blue-500 shadow-lg', badge: 'bg-blue-500 text-white' },
  league:   { border: 'border-purple-500', badge: 'bg-purple-500 text-white' },
  _default: { border: 'border-zinc-200',   badge: 'bg-zinc-500 text-white' },
};

/* ---------- Billing: Promotions ---------- */
/**
 * A promotion is the OFFER, not the grant. Redeeming one writes ordinary
 * feature overrides with an expiry on the customer's billing entity, so there
 * is still exactly one answer to "what can this customer use".
 */
function PromotionsTab() {
  const [promos, setPromos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', feature_keys: [], trial_days: 14, promo_code: '', end_date: '' });
  const [redeeming, setRedeeming] = useState(null);
  const [msg, setMsg] = useState(null);
  const features = useFeatureRegistry();

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 9000); };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/promotions`);
      setPromos(data.promotions || []);
    } catch (e) { flash('error', e.response?.data?.detail || 'Could not load promotions.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/customers`)
      .then(({ data }) => setCustomers((data.customers || []).filter(c => c.type === 'organization')))
      .catch(() => {});
  }, []);

  const toggleKey = (k) => setForm(f => ({
    ...f,
    feature_keys: f.feature_keys.includes(k)
      ? f.feature_keys.filter(x => x !== k)
      : [...f.feature_keys, k],
  }));

  const create = async () => {
    try {
      await axios.post(`${API_URL}/api/promotions`, {
        ...form,
        trial_days: Number(form.trial_days) || 14,
        end_date: form.end_date || null,
        promo_code: form.promo_code || null,
      });
      setShowNew(false);
      setForm({ name: '', description: '', feature_keys: [], trial_days: 14, promo_code: '', end_date: '' });
      load();
    } catch (e) { flash('error', e.response?.data?.detail || 'Could not create the promotion.'); }
  };

  const setActive = async (p2, is_active) => {
    try { await axios.patch(`${API_URL}/api/promotions/${p2.id}`, { is_active }); load(); }
    catch (e) { flash('error', e.response?.data?.detail || 'Could not update.'); }
  };

  const remove = async (p2) => {
    if (!window.confirm(`Delete "${p2.name}"? Trials already granted keep running until they expire.`)) return;
    try { await axios.delete(`${API_URL}/api/promotions/${p2.id}`); load(); }
    catch (e) { flash('error', e.response?.data?.detail || 'Could not delete.'); }
  };

  const redeem = async (p2, orgId) => {
    if (!orgId) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/promotions/${p2.id}/redeem`, { org_id: orgId });
      flash('ok', `${p2.name} granted to ${data.granted_to.name} until ${new Date(data.expires_at).toLocaleDateString()}.`);
      setRedeeming(null); load();
    } catch (e) { flash('error', e.response?.data?.detail || 'Could not redeem.'); }
  };

  const STATE = {
    running:   'bg-emerald-100 text-emerald-700',
    scheduled: 'bg-sky-100 text-sky-700',
    ended:     'bg-zinc-100 text-zinc-600',
    paused:    'bg-amber-100 text-amber-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Promotions</h2>
          <p className="text-sm text-muted-foreground">
            Time-boxed free trials of specific features. Redeeming one grants the features to a
            customer until the trial expires — it never changes their plan.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowNew(v => !v)} data-testid="add-promotion"
          className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <Plus className="h-4 w-4 mr-1" /> New Promotion
        </Button>
      </div>

      {msg && (
        <div className={`text-xs px-3 py-2 rounded ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {showNew && (
        <Card className="bg-card border-border">
          <CardContent className="p-3 space-y-2">
            <div className="text-sm font-semibold">New promotion</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Name</label>
                <Input value={form.name} placeholder="e.g. Spring season trial" data-testid="promo-name"
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Trial days</label>
                <Input type="number" min={1} value={form.trial_days} data-testid="promo-days"
                  onChange={e => setForm(f => ({ ...f, trial_days: e.target.value }))} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Ends (optional)</label>
                <Input type="date" value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Description</label>
              <Input value={form.description} placeholder="What the customer is being offered"
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Features granted ({form.feature_keys.length})
              </label>
              {/* Straight from the registry, so a new sellable feature appears
                  here the moment it is registered. */}
              <div className="flex flex-wrap gap-1.5 mt-1">
                {features.map(f => (
                  <button key={f.key} type="button" onClick={() => toggleKey(f.key)}
                    data-testid={`promo-feature-${f.key}`}
                    className={`text-[11px] px-2 py-1 rounded border transition ${
                      form.feature_keys.includes(f.key)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-background text-muted-foreground border-border hover:border-blue-400'}`}>
                    {f.label || f.key}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 items-end">
              <div className="w-48">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Promo code (optional)</label>
                <Input value={form.promo_code} placeholder="SPRING26"
                  onChange={e => setForm(f => ({ ...f, promo_code: e.target.value }))} />
              </div>
              <Button size="sm" onClick={create} data-testid="promo-create"
                disabled={!form.name.trim() || form.feature_keys.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">Create promotion</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="py-2 px-4">Promotion</th>
              <th className="py-2 px-4">Grants</th>
              <th className="py-2 px-4 text-center">Trial</th>
              <th className="py-2 px-4">Code</th>
              <th className="py-2 px-4 text-center">Redeemed</th>
              <th className="py-2 px-4 text-center">State</th>
              <th className="py-2 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {promos.map(p2 => (
              <tr key={p2.id} className="border-b border-border/60 align-top">
                <td className="py-2 px-4">
                  <div className="font-medium">{p2.name}</div>
                  {p2.description && <div className="text-[11px] text-muted-foreground max-w-md">{p2.description}</div>}
                </td>
                <td className="py-2 px-4">
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {(p2.feature_keys || []).map(k => (
                      <span key={k} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                        {features.find(f => f.key === k)?.label || k}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-2 px-4 text-center text-xs">{p2.trial_days}d</td>
                <td className="py-2 px-4 font-mono text-xs text-muted-foreground">{p2.promo_code || '—'}</td>
                <td className="py-2 px-4 text-center text-xs">{p2.redemption_count}</td>
                <td className="py-2 px-4 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${STATE[p2.state] || STATE.ended}`}>{p2.state}</span>
                </td>
                <td className="py-2 px-4">
                  <div className="flex items-center gap-1 justify-end">
                    {redeeming === p2.id ? (
                      <select autoFocus data-testid={`promo-redeem-select-${p2.id}`}
                        onChange={e => redeem(p2, e.target.value)}
                        onBlur={() => setRedeeming(null)} defaultValue=""
                        className="h-7 text-xs rounded-sm border border-input bg-background px-1">
                        <option value="" disabled>Grant to…</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600"
                        disabled={p2.state !== 'running'} onClick={() => setRedeeming(p2.id)}
                        data-testid={`promo-redeem-${p2.id}`}>Grant</Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                      onClick={() => setActive(p2, !p2.is_active)} data-testid={`promo-toggle-${p2.id}`}>
                      {p2.is_active ? 'Pause' : 'Resume'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500"
                      onClick={() => remove(p2)} data-testid={`promo-del-${p2.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {promos.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground text-sm">
                No promotions yet. A promotion grants named features free for a set number of days.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ---------- Billing: Customer Discounts ---------- */
/**
 * A percentage off the customer's own subscription, stored on whichever entity
 * is billed. OL is the source of truth; the Stripe coupon is created from this
 * at checkout, because a Stripe price is a fixed object and cannot be scaled.
 */
function DiscountsTab({ customers, tiers }) {
  const [rows, setRows] = useState(customers || []);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ discount_percent: 0, discount_expires: '' });
  const [msg, setMsg] = useState(null);

  useEffect(() => { setRows(customers || []); }, [customers]);

  const open = (c) => {
    setEditing(c.id);
    setDraft({
      discount_percent: c.discount_percent || 0,
      discount_expires: (c.discount_expires || '').slice(0, 10),
    });
  };

  const save = async (c) => {
    try {
      await axios.put(`${API_URL}/api/billing/customers/${c.id}/discount`, {
        discount_percent: Number(draft.discount_percent) || 0,
        discount_expires: draft.discount_expires || null,
      });
      setRows(prev => prev.map(x => x.id === c.id
        ? { ...x, discount_percent: Number(draft.discount_percent) || 0,
            discount_expires: draft.discount_expires || null }
        : x));
      setEditing(null);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not save the discount.');
      setTimeout(() => setMsg(null), 8000);
    }
  };

  const priceOf = (t) => (tiers || []).find(x => x.tier === t)?.price_monthly_cents || 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Customer Discounts</h2>
        <p className="text-sm text-muted-foreground">
          A percentage off the customer&apos;s subscription. Set it on a league and every
          organization billed through that league is covered, because the discount lives where
          the plan does.
        </p>
      </div>

      {msg && <div className="text-xs px-3 py-2 rounded bg-red-50 text-red-700">{msg}</div>}

      <Card className="bg-card border-border">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="py-2 px-4">Customer</th>
              <th className="py-2 px-4">Type</th>
              <th className="py-2 px-4">Plan</th>
              <th className="py-2 px-4 text-right">Discount</th>
              <th className="py-2 px-4 text-right">Pays</th>
              <th className="py-2 px-4">Expires</th>
              <th className="py-2 px-4 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const pct = c.discount_percent || 0;
              const expired = c.discount_expires && new Date(c.discount_expires) < new Date();
              const base = priceOf(c.tier);
              const net = Math.round(base * (100 - (expired ? 0 : pct)) / 100);
              const isEditing = editing === c.id;
              return (
                <tr key={c.id} className="border-b border-border/60">
                  <td className="py-2 px-4 font-medium">{c.name}</td>
                  <td className="py-2 px-4 text-xs text-muted-foreground">{c.type}</td>
                  <td className="py-2 px-4 text-xs">
                    {c.tier
                      ? <Badge variant="outline" className="text-[10px] border-border">{c.tier}</Badge>
                      : <span className="text-muted-foreground">unmanaged</span>}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {isEditing ? (
                      <Input type="number" min={0} max={100} className="h-7 w-20 text-xs ml-auto"
                        value={draft.discount_percent} data-testid={`discount-pct-${c.id}`}
                        onChange={e => setDraft(d => ({ ...d, discount_percent: e.target.value }))} />
                    ) : pct > 0 && !expired ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{pct}% OFF</span>
                    ) : expired ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-100 text-zinc-600">Expired</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-4 text-right text-xs">
                    {/* Only a customer on a plan has a price to discount; an
                        unmanaged one would show a meaningless $0 off $0. */}
                    {c.tier ? (
                      <>
                        {pct > 0 && !expired && (
                          <span className="line-through text-muted-foreground mr-1">${(base / 100).toFixed(0)}</span>
                        )}
                        <span className="text-foreground">${(net / 100).toFixed(0)}/mo</span>
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-4 text-xs text-muted-foreground">
                    {isEditing ? (
                      <Input type="date" className="h-7 w-36 text-xs" value={draft.discount_expires}
                        onChange={e => setDraft(d => ({ ...d, discount_expires: e.target.value }))} />
                    ) : c.discount_expires
                      ? new Date(c.discount_expires).toLocaleDateString()
                      : pct > 0 ? 'No expiry' : '—'}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {isEditing ? (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => save(c)} data-testid={`discount-save-${c.id}`}>Save</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => open(c)} data-testid={`discount-edit-${c.id}`}>
                        <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground text-sm">No customers.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ---------- Billing: Payment Settings ---------- */
/**
 * Semprion's merchant services, managed by the Semprion HQ Super User:
 *  - the platform Stripe keys (encrypted, write-only — `utils/stripe_platform.py`),
 *  - Semprion Payments: the fee, card/ACH and OL Finance bank links every org on
 *    the HQ account gets, plus each org's payment setup,
 *  - the Square platform app for orgs that bring their own Square.
 * Orgs choose Semprion Payments or their own Stripe/Square in Registration ▸ Payments.
 */
function PaymentSettingsTab() {
  const card = 'bg-card border-border';
  const inp = 'bg-background border-border';
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Payment Settings</h2>
        <p className="text-sm text-muted-foreground">
          Semprion&apos;s Stripe account takes every org&apos;s payments (dues, sponsorships, store, tickets,
          concessions) into that org&apos;s own bank, and bills each customer&apos;s subscription. An org may
          use its own Stripe or Square instead.
        </p>
      </div>
      <PlatformSetupCard isDark={false} card={card} inp={inp} />
      <HqPaymentsCard isDark={false} card={card} inp={inp} />
      <SquarePlatformCard isDark={false} card={card} inp={inp} />
    </div>
  );
}

/* ---------- Billing: Integrations ---------- */
/**
 * What OL is actually wired to, and whether each one is configured. Every row
 * reports STATUS only and links to where that integration is configured --
 * none of these secrets is editable here, because each already has one proper
 * home and a second editor is how two sources of truth start.
 */
function IntegrationsTab() {
  const [stripe, setStripe] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/stripe-connect/platform`)
      .then(({ data }) => setStripe(data))
      .catch(() => setStripe(null))
      .finally(() => setChecked(true));
  }, []);

  const ROWS = [
    {
      name: 'Stripe (platform)', icon: CreditCard, tint: 'text-violet-600',
      status: stripe?.configured ? `configured (${stripe.mode || 'test'} mode)` : 'not configured',
      ok: !!stripe?.configured,
      where: 'Billing ▸ Payment Settings',
      what: 'Subscription billing for every customer, org payments (Semprion Payments) and OL Finance bank links.',
    },
    {
      name: 'Semprion Payments (per customer)', icon: Building2, tint: 'text-indigo-600',
      status: 'per organization', ok: null,
      where: "Billing ▸ Payment Settings (all orgs) · the org's Registration ▸ Payments",
      what: "Each org takes money into its own bank through Semprion's Stripe, or uses its own Stripe / Square.",
    },
    {
      name: 'Email (Google Workspace SMTP)', icon: Megaphone, tint: 'text-emerald-600',
      status: 'server environment', ok: null,
      where: 'server .env',
      what: 'Member and communications email. Replaced Resend in September.',
    },
    {
      name: 'Facebook / Meta publishing', icon: Megaphone, tint: 'text-sky-600',
      status: 'per organization', ok: null,
      where: 'Communications ▸ News destinations',
      what: 'Publishing a News story to the club Facebook Page.',
    },
    {
      name: 'WordPress bridge', icon: LayoutGrid, tint: 'text-amber-600',
      status: 'per organization', ok: null,
      where: 'Settings ▸ Website',
      what: 'OL pushes the public site; WordPress never calls OL per page load.',
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Integrations</h2>
        <p className="text-sm text-muted-foreground">
          The external services OmniLeagues talks to, and where each one is configured. This page
          reports status; it does not hold a second copy of any credential.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ROWS.map(r => {
          const Icon = r.icon;
          return (
            <Card key={r.name} className="bg-card border-border" data-testid={`integration-${r.name}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`h-4 w-4 shrink-0 ${r.tint}`} />
                    <div className="font-medium text-sm truncate">{r.name}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${
                    r.ok === true ? 'bg-emerald-100 text-emerald-700'
                      : r.ok === false ? 'bg-amber-100 text-amber-700'
                      : 'bg-zinc-100 text-zinc-600'}`}>
                    {checked || r.ok === null ? r.status : 'checking…'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{r.what}</p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Configured in <span className="text-foreground">{r.where}</span>
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Page Catalog (Tools) ---------- */
/**
 * The page catalog decides which dashboard pages each ROLE sees and in what
 * order. Its sync and reseed endpoints existed with no UI at all, which meant
 * the only way to push a menu change was a code deploy plus someone remembering
 * to call an endpoint by hand.
 *
 * Reordering still happens in `frontend/src/config/pageRegistry.js` and still
 * needs a deploy -- the array IS the order. This page is for pushing that
 * deployed registry into the database and for seeing what each role currently
 * gets.
 */
function PageCatalogPane() {
  // The role list comes from the shipped registry, so a new role type appears
  // here the moment it has pages -- never a second hand-maintained list.
  const roles = ROLE_TYPES;
  const [role, setRole] = useState(roles[0] || 'ad');
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 8000); };

  const load = useCallback(async (r) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/view-config/pages/${r}`);
      setPages(data.pages || []);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Could not load the catalog.');
    }
  }, []);

  useEffect(() => { load(role); }, [role, load]);

  const reseed = async () => {
    setBusy('reseed'); setMsg(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/view-config/catalog/reseed`);
      flash('ok', `Reseeded from the shipped file — ${data.upserted} rows, ${data.deactivated} deactivated.`);
      load(role);
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Reseed failed.');
    }
    setBusy('');
  };

  return (
    <div className="space-y-4">
      <Header title="Page Catalog"
        subtitle="Which dashboard pages each role sees, and in what order" />
      {msg && (
        <div className={`text-xs px-3 py-2 rounded ${
          msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      <Card className="bg-card border-border">
        <CardContent className="p-3 text-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2 text-foreground font-medium">
            <AlertCircle className="h-4 w-4 text-sky-500" /> How ordering works
          </div>
          <p>
            The order is the row order in <code>frontend/src/config/pageRegistry.js</code>, so
            reordering a menu means editing that array and deploying. Editing the database
            directly looks like it works and reverts on the next sync.
          </p>
          <p>
            <strong className="text-foreground">Reseed</strong> re-applies the catalog file the
            server ships with. Use it after a restore, or when a role's menu looks wrong.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">Role:</span>
        <select value={role} onChange={e => setRole(e.target.value)} data-testid="catalog-role"
          className="h-9 text-sm rounded-sm border border-input bg-background px-2">
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{pages.length} pages</span>
        <Button size="sm" variant="outline" onClick={reseed} disabled={busy === 'reseed'}
          data-testid="catalog-reseed">
          {busy === 'reseed' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
          Reseed from file
        </Button>
      </div>

      <Card className="bg-card border-border">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="py-2 px-4 w-10">#</th>
              <th className="py-2 px-4">Page</th>
              <th className="py-2 px-4">Category</th>
              <th className="py-2 px-4">Gated by</th>
              <th className="py-2 px-4">State</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(p2 => (
              <tr key={p2.id} className="border-b border-border/60">
                <td className="py-2 px-4 text-xs text-muted-foreground">{p2.sort_order}</td>
                <td className="py-2 px-4">{p2.label}</td>
                <td className="py-2 px-4 text-xs text-muted-foreground">{p2.category}</td>
                <td className="py-2 px-4 text-xs text-muted-foreground">{p2.gate || '—'}</td>
                <td className="py-2 px-4">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${
                    p2.org_disabled ? 'bg-red-100 text-red-700'
                      : p2.feature_state === 'off' ? 'bg-zinc-100 text-zinc-600'
                      : p2.feature_state === 'restrict' ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-100 text-emerald-700'}`}>
                    {p2.org_disabled ? 'org-disabled' : (p2.feature_state || 'on')}
                  </span>
                </td>
              </tr>
            ))}
            {pages.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-sm">
                No pages for this role.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ---------- Billing ---------- */
/**
 * Billing Management, laid out like BackOfficePro's Billing page: an
 * "Available Features" rail you drag onto plan cards, each plan showing the
 * features it includes with an On/Restricted selector, plus Edit Details for
 * price and Stripe id.
 *
 * One difference worth knowing. BOP's page advertises "Auto-sync: changes
 * automatically apply to all companies on that tier", which is literally true
 * and is also how it destroys per-store grants: re-applying a tier rewrites
 * every store's flags. OL resolves entitlements on read, so a plan change
 * reaches every customer on it instantly AND a per-customer override still
 * survives. Same benefit, without the data loss.
 */
// BOP groups Billing Management into these five. Keeping the same grouping
// means the Semprion operator who already knows BOP's console does not have to
// learn a second layout for the same job.
const BILLING_TABS = [
  { id: 'tiers',        label: 'Tier Configurator' },
  { id: 'promotions',   label: 'Promotions' },
  { id: 'discounts',    label: 'Customer Discounts', Icon: Percent },
  { id: 'payments',     label: 'Payment Settings',   Icon: Settings2 },
  { id: 'integrations', label: 'Integrations',       Icon: KeyRound },
];

/** BOP's billing stat card, from its AdminBillingPage.js: a tinted icon tile on
 *  the LEFT, then label / value / optional sub-line. */
function BopStat({ label, value, Icon, tint, fg, sub }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${tint} rounded-lg`}><Icon className={`w-5 h-5 ${fg}`} /></div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BillingPane() {
  const [sub, setSub] = useState('tiers');
  const [tiers, setTiers] = useState([]);
  const [edit, setEdit] = useState({});
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newPlan, setNewPlan] = useState({ tier: '', label: '', price: 0 });
  const [err, setErr] = useState('');
  const features = useFeatureRegistry();

  const loadTiers = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/billing/tiers`);
    setTiers(data.tiers || []);
  }, []);

  useEffect(() => { loadTiers(); }, [loadTiers]);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/customers`)
      .then(({ data }) => setCustomers((data.customers || []).filter(c => c.type !== 'family')))
      .catch(() => setCustomers([]));
  }, []);

  // Every figure below is counted from real customers, not a placeholder. MRR
  // is the sum of the monthly price of the plans actually assigned, which is
  // why it reads $0 until plans are assigned — BOP shows a revenue figure here
  // and an invented one would be worse than an honest zero.
  const stats = useMemo(() => {
    const priceOf = (t) => (tiers.find(x => x.tier === t)?.price_monthly_cents || 0);
    const onPlan = customers.filter(c => c.tier);
    return {
      mrr: onPlan.reduce((a, c) => a + priceOf(c.tier), 0),
      active: customers.filter(c => c.subscription_status === 'active').length,
      trial: customers.filter(c => ['trial', 'trialing'].includes(c.subscription_status)).length,
      pastDue: customers.filter(c => c.subscription_status === 'past_due').length,
      cancelled: customers.filter(c => ['canceled', 'cancelled'].includes(c.subscription_status)).length,
      onPlan: onPlan.length,
      unmanaged: customers.length - onPlan.length,
    };
  }, [customers, tiers]);

  const addPlan = async () => {
    const slug = (newPlan.tier || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (!slug) { setErr('Give the plan a slug.'); return; }
    if (tiers.some(t => t.tier === slug)) { setErr(`A plan with slug "${slug}" already exists.`); return; }
    setErr('');
    try {
      await axios.put(`${API_URL}/api/billing/tiers/${slug}`, {
        label: newPlan.label || slug,
        price_monthly_cents: Math.round(Number(newPlan.price || 0) * 100),
      });
      await loadTiers();
      setAdding(false);
      setNewPlan({ tier: '', label: '', price: 0 });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not create the plan.');
    }
  };

  const save = async (tier) => {
    const patch = edit[tier] || {};
    await axios.put(`${API_URL}/api/billing/tiers/${tier}`, patch);
    await loadTiers();
    setEdit(e => ({ ...e, [tier]: undefined }));
    setEditing(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        {/* Header and stats taken from BOP's AdminBillingPage.js rather than
            approximated: h1 text-2xl, a six-card grid, and each card's icon in
            a tinted tile on the LEFT of a label/value stack. */}
        <div>
          <h1 className="text-2xl font-bold">Billing Management</h1>
          <p className="text-muted-foreground">Manage tiers, features, promotions, and customer billing</p>
        </div>
        <Button size="sm" onClick={() => setAdding(v => !v)} data-testid="add-plan"
          className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <Plus className="h-4 w-4 mr-1" /> Add Plan
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <BopStat label="MRR" value={`$${(stats.mrr / 100).toFixed(2)}`} Icon={DollarSign}
          tint="bg-blue-100" fg="text-blue-600" />
        <BopStat label="Active" value={stats.active} Icon={CheckCircle2}
          tint="bg-green-100" fg="text-green-600" sub={`${stats.onPlan} on a plan`} />
        <BopStat label="Trial" value={stats.trial} Icon={PlayCircle}
          tint="bg-blue-100" fg="text-blue-600" sub="in trial" />
        <BopStat label="Past due" value={stats.pastDue} Icon={Clock}
          tint="bg-amber-100" fg="text-amber-600" sub="Payment pending" />
        <BopStat label="Cancelled" value={stats.cancelled} Icon={XCircle}
          tint="bg-red-100" fg="text-red-600" sub="Non-payment" />
        <BopStat label="Unmanaged" value={stats.unmanaged} Icon={AlertCircle}
          tint="bg-zinc-100" fg="text-zinc-600" sub="every feature on" />
      </div>

      {/* BOP's TabsList: a muted pill rail with a white active chip and an
          icon on the last three triggers. */}
      <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
        {BILLING_TABS.map(t => {
          const Icon = t.Icon;
          return (
            <button key={t.id} onClick={() => setSub(t.id)} data-testid={`billing-tab-${t.id}`}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1
                          text-sm font-medium transition-all ${
                sub === t.id ? 'bg-background text-foreground shadow' : 'hover:text-foreground'}`}>
              {Icon && <Icon className="w-4 h-4 mr-1.5" />}
              {t.label}
            </button>
          );
        })}
      </div>

      {sub === 'promotions'   && <PromotionsTab />}
      {sub === 'discounts'    && <DiscountsTab customers={customers} tiers={tiers} />}
      {sub === 'payments'     && <PaymentSettingsTab />}
      {sub === 'integrations' && <IntegrationsTab />}

      {sub === 'tiers' && (<>
      {adding && (
        <Card className="bg-card border-border">
          <CardContent className="p-3 space-y-2">
            <div className="text-sm font-semibold">New plan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Slug</label>
                <Input value={newPlan.tier} placeholder="e.g. enterprise" data-testid="new-plan-slug"
                  onChange={e => setNewPlan(p2 => ({ ...p2, tier: e.target.value }))} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Name</label>
                <Input value={newPlan.label} placeholder="e.g. Enterprise" data-testid="new-plan-label"
                  onChange={e => setNewPlan(p2 => ({ ...p2, label: e.target.value }))} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Monthly price (USD)</label>
                <Input type="number" min={0} value={newPlan.price} data-testid="new-plan-price"
                  onChange={e => setNewPlan(p2 => ({ ...p2, price: e.target.value }))} />
              </div>
            </div>
            {err && <p className="text-[11px] text-red-600">{err}</p>}
            <div className="flex gap-2">
              <Button size="sm" onClick={addPlan} data-testid="new-plan-create"
                className="bg-blue-600 hover:bg-blue-700 text-white">Create plan</Button>
              <Button size="sm" variant="outline" onClick={() => { setAdding(false); setErr(''); }}>Cancel</Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              A new plan starts with no features granted, so nothing changes for anyone until you
              drag features onto it and assign it to a customer.
            </p>
          </CardContent>
        </Card>
      )}

      {/* BOP's blue guidance card, same classes: bg-blue-50 / border-blue-200,
          Info icon blue-600, body blue-800 over a blue-700 bullet list. */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How to use the plan configurator:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Add features:</strong> drag one from the left panel onto a plan, or use its check box</li>
                <li><strong>Remove features:</strong> hover a feature and click the X to take it off the plan</li>
                <li><strong>Access levels:</strong> set each feature to On or Restricted &mdash; restricted shows greyed rather than hidden</li>
                <li><strong>Edit plan:</strong> Edit Details changes the name, prices, description, badge and Stripe id, and whether the plan is still offered</li>
                <li><strong>Rename a feature:</strong> hover one in the left panel and click the pencil — it changes what customers see, never the key that gates it</li>
                <li><strong>Applies immediately:</strong> every customer on the plan picks the change up on their next page load, and any per-customer override still wins</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <FeatureRail features={features} onRenamed={() => window.location.reload()} />
        </div>
        {/* Four plans across, as BOP lays them out — the point of the layout is
            comparing plans at a glance, which needs them side by side. */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {tiers.map(t => {
            const accent = TIER_ACCENT[t.tier] || TIER_ACCENT._default;
            return (
              <Card key={t.tier} className={`border-2 ${accent.border}`} data-testid={`tier-card-${t.tier}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-lg font-semibold leading-tight">
                      {t.label || t.tier}
                      {t.is_active === false && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                          not offered
                        </span>
                      )}
                    </div>
                    {t.badge_text && (
                      <Badge className={`${accent.badge} text-[10px] shrink-0`}>{t.badge_text}</Badge>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-muted-foreground leading-snug">{t.description}</p>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">
                      ${((t.price_monthly_cents || 0) / 100).toFixed(0)}
                    </span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                    {t.price_yearly_cents ? (
                      <span className="text-xs text-muted-foreground ml-2">
                        (${(t.price_yearly_cents / 100).toFixed(0)}/yr)
                      </span>
                    ) : null}
                  </div>

                  <TierFeatureGrants tier={t} features={features} onSaved={loadTiers} />

                  {editing === t.tier ? (
                    <div className="space-y-2 pt-2 border-t border-border">
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Tier name</label>
                        <Input defaultValue={t.label || t.tier}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), label: e.target.value } }))}
                          data-testid={`edit-tier-name-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Monthly price (USD)</label>
                        <Input type="number" min={0}
                          defaultValue={((t.price_monthly_cents || 0) / 100).toFixed(0)}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), price_monthly_cents: Math.round(Number(e.target.value) * 100) } }))}
                          data-testid={`tier-price-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Yearly price (USD)</label>
                        <Input type="number" min={0}
                          defaultValue={t.price_yearly_cents ? (t.price_yearly_cents / 100).toFixed(0) : ''}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), price_yearly_cents: Math.round(Number(e.target.value) * 100) } }))}
                          data-testid={`tier-yearly-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Description</label>
                        <Input defaultValue={t.description || ''}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), description: e.target.value } }))}
                          data-testid={`tier-desc-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Badge</label>
                        <Input defaultValue={t.badge_text || ''} placeholder="e.g. Popular"
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), badge_text: e.target.value } }))}
                          data-testid={`tier-badge-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Badge colour</label>
                        <select defaultValue={t.badge_color || 'blue'}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), badge_color: e.target.value } }))}
                          data-testid={`edit-tier-badge-color-${t.tier}`}
                          className="w-full h-9 text-sm rounded-sm border border-input bg-background px-2">
                          {['blue', 'green', 'amber', 'purple', 'red', 'zinc'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Active</label>
                          {/* Withdrawing a plan must never downgrade anyone: an
                              inactive tier simply stops being offered, and every
                              customer already on it keeps exactly what they have. */}
                          <p className="text-[10px] text-muted-foreground">
                            Off = no longer offered. Customers on it are unaffected.
                          </p>
                        </div>
                        <Switch defaultChecked={t.is_active !== false}
                          onCheckedChange={v => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), is_active: v } }))}
                          data-testid={`edit-tier-active-${t.tier}`} />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Stripe Price ID</label>
                        <Input defaultValue={t.stripe_price_id || ''}
                          onChange={e => setEdit(x => ({ ...x, [t.tier]: { ...(x[t.tier] || {}), stripe_price_id: e.target.value } }))}
                          placeholder="price_..." data-testid={`tier-stripe-${t.tier}`} />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => save(t.tier)} disabled={!edit[t.tier]}
                          className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40" data-testid={`tier-save-${t.tier}`}>
                          <Save className="h-3 w-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full"
                      onClick={() => setEditing(t.tier)} data-testid={`edit-tier-${t.tier}`}>
                      <Settings2 className="w-4 h-4 mr-2" /> Edit Details
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {/* BOP puts Add Tier at the FOOT of the tier column, not in the
              header — the header button stays as well, as BOP has both. */}
          <button type="button" onClick={() => setAdding(true)} data-testid="add-tier-btn"
            className="min-h-[120px] rounded-lg border-2 border-dashed border-border
                       text-muted-foreground hover:border-blue-400 hover:text-blue-600
                       transition-colors flex items-center justify-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Add Tier
          </button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        Stripe Checkout is wired. Set <code className="text-foreground">STRIPE_SECRET_KEY</code> in server env + paste real price IDs above to enable live billing.
      </div>
      </>)}
    </div>
  );
}

/* The console's legacy "Tickets" pane lived here. It read
 * `/api/admin/tickets` (support_tickets) while the Support Center's own
 * Tickets tab read an empty `helpdesk_tickets` -- so the legacy pane was
 * the one showing OL's real tickets. Both now read `support_tickets`, and
 * the two columns only this pane had (who raised it, and the captured
 * context) moved into SupportCommandCenter's table. One tickets surface. */

/* ---------- System Messages ---------- */
function MessagesPane() {
  const [msgs, setMsgs] = useState([]);
  const [form, setForm] = useState({ title: '', body: '', severity: 'info', audience: 'all', active: true });
  const refresh = () => axios.get(`${API_URL}/api/admin/system-messages`).then(({ data }) => setMsgs(data.messages || []));
  useEffect(() => { refresh(); }, []);
  const create = async () => {
    if (!form.title || !form.body) return;
    await axios.post(`${API_URL}/api/admin/system-messages`, form);
    setForm({ title: '', body: '', severity: 'info', audience: 'all', active: true });
    refresh();
  };
  const remove = async (id) => { await axios.delete(`${API_URL}/api/admin/system-messages/${id}`); refresh(); };

  return (
    <div className="space-y-4">
      <Header title="System Messages" subtitle="Org-wide announcements shown to every user" />
      <Card className="bg-card border-border p-4 space-y-3">
        <Input placeholder="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          className="bg-background border-border" data-testid="sysmsg-title" />
        <Input placeholder="Message body" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
          className="bg-background border-border" data-testid="sysmsg-body" />
        <div className="flex gap-2">
          <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}
            className="bg-background border border-border text-foreground px-2 h-9 text-sm rounded">
            {['info', 'warning', 'critical'].map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={form.audience} onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}
            className="bg-background border border-border text-foreground px-2 h-9 text-sm rounded">
            {['all', 'leagues', 'organizations', 'families'].map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <Button onClick={create} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="sysmsg-create">
            <Plus className="h-4 w-4 mr-1" /> Post
          </Button>
        </div>
      </Card>
      <div className="space-y-2">
        {msgs.map(m => (
          <Card key={m.id} className="bg-card border-border p-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">{m.title}</div>
              <div className="text-xs text-muted-foreground">{m.body}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{m.severity} · {m.audience}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="text-red-400 hover:text-red-300">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Card>
        ))}
        {msgs.length === 0 && <div className="text-center text-muted-foreground text-sm py-8">No messages posted.</div>}
      </div>
    </div>
  );
}

/* ---------- Staff ---------- */
function MembersPane() {
  // Corporate (HQ) OL users only. Customer members are managed by each Tech Specialist;
  // league members by each League Admin.
  return (
    <div className="space-y-4">
      <Header title="User Management" subtitle="Corporate (HQ) OmniLeagues users" />
      <OLMembersPanel scope="corporate" />
    </div>
  );
}

/* ---------- Audit ---------- */
function AuditPane() {
  const [events, setEvents] = useState([]);
  useEffect(() => { axios.get(`${API_URL}/api/admin/audit?limit=200`).then(({ data }) => setEvents(data.events || [])); }, []);
  return (
    <div className="space-y-4">
      <Header title="Audit Log" subtitle="Every HQ action, most recent first" />
      <Card className="bg-card border-border">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr><th className="py-2 px-4">When</th><th className="py-2 px-4">Event</th><th className="py-2 px-4">Actor</th><th className="py-2 px-4">Target</th><th className="py-2 px-4">Details</th></tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id || e.created_at} className="border-b border-border align-top">
                <td className="py-2 px-4 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
                <td className="py-2 px-4"><span className="text-xs text-blue-600">{e.event}</span></td>
                <td className="py-2 px-4 text-xs text-muted-foreground">{e.actor_user_id?.slice(0, 8) || '—'}</td>
                <td className="py-2 px-4 text-xs text-muted-foreground">{e.target_user_id?.slice(0, 8) || '—'}</td>
                <td className="py-2 px-4 text-xs text-muted-foreground"><code className="text-[10px]">{JSON.stringify(e.details || {}).slice(0, 80)}</code></td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-sm">No events.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ---------- shared ---------- */
function Header({ title, subtitle }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}
function Alert({ msg, type }) {
  const palette = type === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300';
  const Icon = type === 'error' ? XCircle : CheckCircle2;
  return (
    <div className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm ${palette}`}>
      <Icon className="h-4 w-4" /><span>{msg}</span>
    </div>
  );
}
